import { useState, useEffect } from "react"
import { cn } from "@/lib/utils"
import { scrollToSection } from "@/lib/scroll-to-section"

const navItems = [
  { id: "hero", label: "Home" },
  { id: "partners", label: "Tools&Foundries" },
  { id: "gap", label: "Gap" },
  { id: "about", label: "About" },
  { id: "why-aexiom", label: "Why Us" },
  { id: "work", label: "Capability" },
  { id: "credibility", label: "Team" },
  { id: "proof", label: "Case Studies" },
  { id: "reassure", label: "Reassure" },
  { id: "differentiate", label: "Differentiate" },
  { id: "tape-outs", label: "Tape-Outs" },
  { id: "close", label: "Contact" },
  { id: "colophon", label: "Footer" },
]

export function SideNav() {
  const [activeSection, setActiveSection] = useState("hero")

  useEffect(() => {
    const sectionElements = navItems
      .map(({ id }) => document.getElementById(id))
      .filter((element): element is HTMLElement => element !== null)

    const updateActiveSection = () => {
      const viewportHeight = window.innerHeight
      const targetLine = viewportHeight * 0.32
      let bestId = sectionElements[0]?.id ?? "hero"
      let bestScore = -Infinity

      sectionElements.forEach((section) => {
        const rect = section.getBoundingClientRect()
        if (rect.bottom <= 0 || rect.top >= viewportHeight) return

        const distanceFromTarget = Math.abs(rect.top - targetLine)
        const isPastTarget = rect.top <= targetLine ? 1 : 0
        const score = isPastTarget * 1000 - distanceFromTarget

        if (score > bestScore) {
          bestScore = score
          bestId = section.id
        }
      })

      setActiveSection((currentActive) => (currentActive !== bestId ? bestId : currentActive))
    }

    const observer = new IntersectionObserver(
      () => {
        updateActiveSection()
      },
      {
        threshold: [0, 0.25, 0.5, 0.75, 1],
        rootMargin: "-45% 0px -45% 0px",
      },
    )

    sectionElements.forEach((section) => observer.observe(section))
    updateActiveSection()

    window.addEventListener("scroll", updateActiveSection, { passive: true })
    window.addEventListener("resize", updateActiveSection)

    return () => {
      observer.disconnect()
      window.removeEventListener("scroll", updateActiveSection)
      window.removeEventListener("resize", updateActiveSection)
    }
  }, [])

  return (
    <nav className="fixed left-0 top-0 z-50 h-screen w-16 md:w-20 hidden md:flex flex-col justify-center border-r border-border/30 bg-background/80 backdrop-blur-sm">
      <div className="flex flex-col gap-3 px-4">
        {navItems.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            onClick={() => scrollToSection(id)}
            className="group relative flex h-5 w-10 cursor-pointer items-center gap-3 focus-visible:outline-none"
            aria-label={`Go to ${label}`}
          >
            <span
              className={cn(
                "h-1.5 w-1.5 shrink-0 rounded-full transition-all duration-300 group-hover:scale-125 group-hover:bg-accent group-focus-visible:scale-125 group-focus-visible:bg-accent",
                activeSection === id
                  ? "bg-accent scale-125"
                  : "bg-muted-foreground/40",
              )}
            />
            <span
              className={cn(
                "pointer-events-none absolute left-6 z-50 whitespace-nowrap border border-accent/30 bg-background/95 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-accent shadow-[0_0_24px_oklch(0.7_0.2_45_/_0.14)] backdrop-blur-md",
                "translate-x-0 transition-all duration-200",
                activeSection === id 
                  ? "left-8 opacity-100"
                  : "group-hover:left-8 group-hover:opacity-100 group-focus-visible:left-8 group-focus-visible:opacity-100 opacity-0",
              )}
            >
              {label}
            </span>
          </button>
        ))}
      </div>
    </nav>
  )
}
